import { apiFetch } from '../../../shared/services/apiClient';
import {
  Producto,
  CreateProductoInput,
  UpdateProductoInput,
  ProductoListResponse,
  ApiResponse,
  InactivarProductoInput,
  InactivarProductoResponse,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export class ProductoService {
  static async crearProducto(input: CreateProductoInput): Promise<Producto> {
    const response = await apiFetch(`${API_URL}/productos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const data: ApiResponse<Producto> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al crear producto');
    }

    return data.data!;
  }

  static async listarProductos(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<ProductoListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });

    const response = await apiFetch(`${API_URL}/productos?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const data: ApiResponse<ProductoListResponse> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al listar productos');
    }

    return data.data!;
  }

  static async obtenerProducto(id: number): Promise<Producto> {
    const response = await apiFetch(`${API_URL}/productos/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const data: ApiResponse<Producto> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Producto no encontrado');
    }

    return data.data!;
  }

  static async actualizarProducto(
    id: number,
    input: UpdateProductoInput
  ): Promise<Producto> {
    const response = await apiFetch(`${API_URL}/productos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const data: ApiResponse<Producto> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al actualizar producto');
    }

    return data.data!;
  }

  static async eliminarProducto(id: number): Promise<void> {
    const response = await apiFetch(`${API_URL}/productos/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    const data: ApiResponse<void> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al eliminar producto');
    }
  }

  static async inactivarProducto(id: number): Promise<Producto> {
    const input: InactivarProductoInput = { activo: false };
    const response = await apiFetch(`${API_URL}/productos/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const data: InactivarProductoResponse = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al inactivar producto');
    }

    return data.data!;
  }

  static async buscarProductos(query: string): Promise<Producto[]> {
    const params = new URLSearchParams({ q: query });

    const response = await apiFetch(`${API_URL}/productos/buscar?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const data: ApiResponse<Producto[]> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al buscar productos');
    }

    return data.data!;
  }

  // Métodos de Imagen
  static async getImagenes(
    productoId: number
  ): Promise<any[]> {
    const response = await apiFetch(`${API_URL}/productos/${productoId}/imagenes`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const data: ApiResponse<any[]> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al obtener imágenes');
    }

    // Mapear respuesta a ImagenProducto
    return (data.data || []).map((img: any) => ({
      id: String(img.id),        // Convertir ID del servidor a string
      imagenId: img.id,          // Guardar ID real en BD
      file: undefined,           // No hay file para imágenes del servidor
      preview: img.imagenData,   // Ya tiene prefijo data:...;base64,...
      nombre: img.nombreArchivo,
      tamaño: img.tamañoBytes,
      tipo: img.mimeType,
    }));
  }

  static async subirImagen(
    productoId: number,
    imagen: any
  ): Promise<{ id: number }> {
    // Extraer base64 puro (sin prefijo data:...)
    const base64Part = imagen.preview.includes(',') ? imagen.preview.split(',')[1] : imagen.preview;

    const response = await apiFetch(`${API_URL}/productos/${productoId}/imagenes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        imagenData: base64Part,
        mimeType: imagen.tipo,
        nombreArchivo: imagen.nombre,
        tamañoBytes: imagen.tamaño,
      }),
    });

    const data: ApiResponse<any> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al subir imagen');
    }

    return { id: data.data.id };
  }

  static async eliminarImagen(
    productoId: number,
    imagenId: number
  ): Promise<void> {
    const response = await apiFetch(
      `${API_URL}/productos/${productoId}/imagenes/${imagenId}`,
      {
        method: 'DELETE',
      }
    );

    const data: ApiResponse<void> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Error al eliminar imagen');
    }
  }
}
